import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, apiRequest } from '@/lib/api-client'
import { useAuth } from '@/features/auth/auth-provider'
import { useWorkspace, type WorkspaceRole } from '@/features/workspace/workspace-provider'

export type { WorkspaceRole }

const membersResponseSchema = z.object({
  members: z.array(
    z.object({
      user_id: z.string(),
      email: z.string().nullable().optional(),
      role: z.enum(['owner', 'admin', 'member', 'viewer']),
    }),
  ),
})

export interface WorkspaceRoleInfo {
  /** Rolle des aktuellen Users; null solange unbekannt. */
  role: WorkspaceRole | null
  isLoading: boolean
  /** member+ darf Outputs zur Review anmelden. */
  canRequestReview: boolean
  /** owner/admin duerfen Reviews entscheiden. */
  canReview: boolean
  /** user_id -> E-Mail (nur fuer owner/admin aufloesbar, sonst leer). */
  memberEmails: Map<string, string>
}

/**
 * Leitet die Workspace-Rolle des aktuellen Users ab. EINZIGE Rollenquelle ist
 * der WorkspaceProvider (role-Feld von GET /api/workspaces/me, Query-Key
 * ['workspace', 'current']) — kein eigener /workspaces/me-Fetch mehr (M5-B).
 * Als Fallback (aelteres Backend ohne role-Feld) dient die bisherige
 * Heuristik ueber /workspace/members: die Liste ist owner/admin-only, ein 403
 * heisst "kein Reviewer" (member/viewer). Die Mitgliederliste liefert zudem
 * memberEmails fuer Reviewer-Labels. Die eigentliche Durchsetzung bleibt
 * beim Backend (403 auf den Endpunkten).
 */
export function useWorkspaceRole(): WorkspaceRoleInfo {
  const { user, isAuthenticated } = useAuth()
  const { role: workspaceRole, isLoading: workspaceLoading } = useWorkspace()

  const membersQuery = useQuery({
    queryKey: ['workspace', 'members', 'self-role'],
    enabled: isAuthenticated && !!user,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      try {
        return await apiRequest({ path: '/workspace/members', schema: membersResponseSchema })
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) return { members: [] }
        throw err
      }
    },
  })

  return useMemo(() => {
    const members = membersQuery.data?.members ?? []
    const self = user ? members.find((m) => m.user_id === user.id) : undefined
    // 403-Heuristik (Fallback): owner/admin sehen die Mitgliederliste inkl.
    // eigener Zeile; member/viewer bekommen 403 und landen auf 'member'.
    const heuristicRole: WorkspaceRole | null =
      membersQuery.data === undefined ? null : (self?.role ?? 'member')
    const role: WorkspaceRole | null = workspaceRole ?? heuristicRole
    const memberEmails = new Map<string, string>()
    for (const member of members) {
      if (member.email) memberEmails.set(member.user_id, member.email)
    }
    return {
      role,
      isLoading: workspaceLoading || membersQuery.isLoading,
      canRequestReview: role === 'owner' || role === 'admin' || role === 'member',
      canReview: role === 'owner' || role === 'admin',
      memberEmails,
    }
  }, [workspaceRole, workspaceLoading, membersQuery.data, membersQuery.isLoading, user])
}
